import classnames from "classnames";
import React, { MouseEvent, useState } from "react";

import { isDefined } from "@utils/helpers";

import {
    toHierarchicalFacet,
    toHierarchicalFacetPath,
} from "@services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";

import { RecursiveHierarchicalFacet } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import { extractValue } from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/HierarchicalFacet/hierarchicalFacet.helpers";
import { HIERARCHICAL_FACET_SHOWMORE_LIMIT } from "@components/Search/SearchResults/StateComponents/Facets/Facet/facet.configuration";
import { ShowMoreComponent } from "@components/Search/SearchResults/components/ShowMore/showMore.component";

import "./hierarchicalFacet.scss";

type HierarchicalFacetComponentProps = {
    childValues: Record<string, RecursiveHierarchicalFacet>;
    refine: (level: number, value: string) => void;
    selectedValue?: string;
    level: number;
    selectedValueLevel?: number;
};

const HierarchicalFacetComponent = ({
    childValues,
    refine,
    level,
    selectedValue,
    selectedValueLevel,
}: HierarchicalFacetComponentProps) => {
    const [isShowingMore, setIsShowingMore] = useState(false);

    const [optimisticValue, setOptimisticValue] = useState<string | undefined>(
        selectedValue,
    );

    const childEntries = Object.entries(childValues ?? {}).filter(
        ([_, childFacet]) => {
            if (!isDefined(selectedValue)) return true;

            const levelUp = toHierarchicalFacet(
                toHierarchicalFacetPath(selectedValue).slice(0, -1),
            );

            return (
                childFacet.facetValue.startsWith(levelUp) ||
                levelUp.startsWith(childFacet.facetValue)
            );
        },
    );

    const selectedValueIndex = childEntries.findIndex(([_, childFacet]) =>
        selectedValue?.startsWith(childFacet.facetValue),
    );

    const mustShowMore = selectedValueIndex > HIERARCHICAL_FACET_SHOWMORE_LIMIT;
    const canShowMore =
        childEntries.length > HIERARCHICAL_FACET_SHOWMORE_LIMIT &&
        (selectedValueLevel === level || !isDefined(selectedValueLevel));

    const childComponents = childEntries
        .map(([formattedValue, childFacet]) => {
            const childLevel = [...childFacet.facetValue.matchAll(/\s>\s/g)]
                .length;

            const handleOnClick = (e: MouseEvent) => {
                e.stopPropagation();
                setOptimisticValue(childFacet.facetValue);
                refine(childLevel, childFacet.facetValue);
            };

            const isSelected = optimisticValue === childFacet.facetValue;
            const hasChildren = !!Object.keys(childFacet.childValues ?? {})
                .length;

            const open =
                hasChildren && selectedValue?.startsWith(childFacet.facetValue);

            const { identifier, textValue } = extractValue(formattedValue);

            return (
                <li
                    key={identifier}
                    className={"cursor-pointer align-items-center"}
                    title={textValue}
                >
                    <button
                        role="button"
                        className={classnames(
                            "d-flex align-items-center",
                            isSelected
                                ? "font-weight-bold"
                                : "font-weight-normal",
                        )}
                        tabIndex={0}
                        onClick={handleOnClick}
                    >
                        <div
                            className={
                                "truncate__wrapper d-flex align-items-center"
                            }
                        >
                            {open && (
                                <i className="las la-angle-down mr-1 d-inline"></i>
                            )}
                            {!open && (
                                <i className="las la-angle-right mr-1 d-inline"></i>
                            )}
                            <div className={"truncate"}>{textValue}</div>
                        </div>
                        <small className="pl-2 d-inline">
                            ({childFacet.count})
                        </small>
                    </button>

                    {open && (
                        <HierarchicalFacetComponent
                            childValues={childFacet.childValues}
                            refine={refine}
                            level={level + 1}
                            selectedValue={selectedValue}
                            selectedValueLevel={selectedValueLevel}
                        />
                    )}
                </li>
            );
        })
        .slice(
            0,
            mustShowMore || isShowingMore
                ? undefined
                : HIERARCHICAL_FACET_SHOWMORE_LIMIT,
        );

    return (
        <>
            <ul
                className={classnames(
                    "list-unstyled mb-0 hierarchicalFacet",
                    level && "pl-3",
                )}
            >
                {childComponents}
            </ul>
            {canShowMore && !mustShowMore && (
                <ShowMoreComponent
                    showMore={() => setIsShowingMore(prevState => !prevState)}
                    isShowingMore={isShowingMore}
                    className={classnames(level && "pl-3")}
                />
            )}
        </>
    );
};

export { HierarchicalFacetComponent };
